import { z } from 'zod';
import { Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from 'lib/trpcClient';
import { FIRM } from 'adminConstants/routes';
import PageContainer from 'components/PageContainer';
import GeneralInformation from './GeneralInformation';
import PageContent from 'components/PageContent';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import RecordView from 'components/RecordView';
import { lpFirm as rules } from 'further-ui/validations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Alert, AlertTitle, Typography } from '@mui/material';

type FormValues = z.infer<typeof rules.Create>;

const LpFirmAddEditScreen: React.FC = () => {
  const mutationOpts = useHandleTrpcMutation();

  const createLpFirm = api.lpFirm.create.useMutation(
    mutationOpts({
      successMessage: 'LP Firm created successfully.',
      successRedirect: FIRM,
    }),
  );

  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Firms', link: '/firm' },
    { label: 'Add LP Firm', link: '/', isActive: true },
  ];

  const formMethods = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.Create),
    values: {
      firmName: '',
      addCurrentUserToOrganization: false,
      newUserRole: undefined,
    },
    criteriaMode: 'all',
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = formMethods;

  const isAddingCurrentUserToOrganization = watch(
    'addCurrentUserToOrganization',
  );

  const onSubmit = async (data: FormValues) => {
    createLpFirm.mutate(data);
  };

  return (
    <PageContainer
      heading="Add LP Firm"
      loading={createLpFirm.isPending}
      breadcrumbs={breadcrumbs}
    >
      <PageContent>
        <FormProvider {...formMethods}>
          <form>
            <RecordView size="full">
              <GeneralInformation
                control={control}
                errors={errors}
                isAddingCurrentUserToOrganization={
                  isAddingCurrentUserToOrganization
                }
              />

              <Alert severity="info">
                <AlertTitle>Next steps</AlertTitle>
                <Typography variant="body2">
                  Once an LP firm is added, it can be administered by switching
                  organisations and then navigating to the firm settings page.
                </Typography>
                <Typography variant="body2">
                  If you don't add yourself as a user to the firm now, you will
                  need to add yourself as a user to the firm manually later on.
                </Typography>
                <Typography variant="body2">
                  If this firm is linked to an EIS firm, you will need to create
                  a linked portal after creation.
                </Typography>
              </Alert>

              <ButtonGroup>
                <Link to={FIRM}>
                  <Button variant="outlined">Cancel</Button>
                </Link>
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  loading={createLpFirm.isPending}
                >
                  Save
                </Button>
              </ButtonGroup>
            </RecordView>
          </form>
        </FormProvider>
      </PageContent>
    </PageContainer>
  );
};

export default LpFirmAddEditScreen;
